import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip, LinearProgress } from '@mui/material';
import { AccessTime, LocationOn, QrCodeScanner, Timer } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import GradientSurface from './shared/GradientSurface';
import SessionTicker from './shared/SessionTicker';
import { useActiveSession } from '../hooks/useActiveSession';
import { RADIUS } from '../theme/tokens';

const MotionBox = motion(Box);

/**
 * ActiveSessionBanner
 * Shows the user's active library check-in. Mounted once in StudentPortal shell.
 *
 * @param {boolean} [compact=false] - compact layout for persistent sticky position
 * @param {boolean} [sticky=false]  - sticky positioning below shell header
 */
function ActiveSessionBanner({ compact = false, sticky = false }) {
  const navigate = useNavigate();
  const { session, loading } = useActiveSession();
  const [timeInfo, setTimeInfo] = useState(null);

  // Remaining time is a separate lightweight fetch — only when session exists
  useEffect(() => {
    if (!session) return;

    let cancelled = false;
    const fetchRemaining = async () => {
      try {
        // Dynamic import to avoid circular dep on api at module level
        const { default: api } = await import('../utils/api');
        const res = await api.get('/sessions/remaining-time');
        if (!cancelled) setTimeInfo(res.data?.data ?? null);
      } catch {
        // Non-critical; ignore silently
      }
    };

    fetchRemaining();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const getRemainingColor = (minutes) => {
    if (minutes <= 5) return '#EF4444';
    if (minutes <= 15) return '#F97316';
    if (minutes <= 30) return '#EAB308';
    return '#4ADE80';
  };

  if (loading || !session) return null;

  return (
    <MotionBox
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.24 }}
      sx={
        sticky
          ? { position: 'sticky', top: 0, zIndex: 9, mx: { xs: 0, md: 2 } }
          : {}
      }
    >
      <GradientSurface
        variant="brand"
        animated
        radius={compact ? 0 : RADIUS.lg}
        sx={{ overflow: 'hidden', mb: compact ? 0 : 2 }}
      >
        <LinearProgress
          sx={{
            height: 3,
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': { bgcolor: '#4ADE80' },
          }}
        />

        <Box sx={{ p: compact ? 1.5 : 2.5 }}>
          {/* Header row */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={compact ? 0.5 : 1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#4ADE80',
                  animation: 'bmlPulse 2s infinite',
                }}
              />
              <Typography
                variant={compact ? 'body2' : 'h6'}
                fontWeight={700}
                color="white"
              >
                Active Session
              </Typography>
            </Box>
            <Chip
              label="CHECKED IN"
              size="small"
              sx={{ bgcolor: '#4ADE80', color: 'white', fontWeight: 700, fontSize: '0.65rem' }}
            />
          </Box>

          {/* Library name */}
          <Box display="flex" alignItems="center" gap={0.75} mb={compact ? 0.5 : 1}>
            <LocationOn sx={{ fontSize: 18, color: 'rgba(255,255,255,0.9)' }} />
            <Typography variant={compact ? 'body2' : 'body1'} fontWeight={600} color="white" noWrap>
              {session.library?.name || 'Library'}
            </Typography>
          </Box>

          {/* Duration ticker */}
          <Box display="flex" alignItems="center" gap={0.75} mb={compact ? 0 : (timeInfo && !timeInfo.unlimited ? 1 : 2)}>
            <AccessTime sx={{ fontSize: 18, color: 'rgba(255,255,255,0.9)' }} />
            <Typography variant="body2" color="rgba(255,255,255,0.9)">
              Duration:{' '}
              <strong>
                <SessionTicker enteredAt={session.entered_at} showSeconds={!compact} />
              </strong>
            </Typography>
          </Box>

          {/* Remaining time (non-compact, hours-limited plans) */}
          {!compact && timeInfo && !timeInfo.unlimited && timeInfo.remainingMinutes !== undefined && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              sx={{
                bgcolor: 'rgba(0,0,0,0.2)',
                borderRadius: RADIUS.sm,
                p: 1.5,
                border: timeInfo.remainingMinutes <= 15 ? `2px solid ${getRemainingColor(timeInfo.remainingMinutes)}` : 'none',
              }}
            >
              <Timer sx={{ fontSize: 18, color: getRemainingColor(timeInfo.remainingMinutes) }} />
              <Box flex={1}>
                <Typography variant="caption" color="rgba(255,255,255,0.7)" display="block">
                  Today's Limit: {timeInfo.hoursPerDay}h/day
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color: getRemainingColor(timeInfo.remainingMinutes) }}
                >
                  {timeInfo.remainingMinutes > 0
                    ? `${timeInfo.remainingMinutes} min remaining`
                    : 'Daily limit reached'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Check-out CTA */}
          {!compact && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<QrCodeScanner />}
              onClick={() => navigate('/student/scanner')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 700,
                py: 1.25,
                borderRadius: RADIUS.md,
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              Scan to Check Out
            </Button>
          )}

          {compact && (
            <Box
              component="span"
              onClick={() => navigate('/student/scanner')}
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.75rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                display: 'block',
                mt: 0.5,
              }}
            >
              Tap to check out
            </Box>
          )}
        </Box>
      </GradientSurface>
    </MotionBox>
  );
}

export default ActiveSessionBanner;
